import { useEffect, useState, useCallback, CSSProperties } from "react";
import { trackPixelEvent } from "../../../utils/pixel";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import { subscribeToProduct } from "../../../api/apiEndpoints";
import {
  clearNextProductCall,
  attachPaymentMethodToSubscriptionCall,
  trackPurchaseConversionCall,
} from "../../../api/apiOperations";
import DiscountCodeInput from "../../../components/DiscountCodeInput";
import {
  setSubscriptionId,
  setClientSecretStripe,
  clearAppliedDiscount,
  clearClientSecretStripe,
  clearSubscriptionId,
  emptyBasket,
} from "../../../slices/appSlice";
import { setLoggedIn, setMemberLoggedIn } from "../../../slices/authSlice";
import { DASHBOARD_MEMBERS } from "../../../utils/routes";

// ── Types ──
type RenewalTier = {
  key: 'monthly' | 'quarterly' | 'yearly';
  label: string;
  rate: number;
  term: string;
  note: string;
  savings: number | null;
  businessProductId: string;
};

// ── Inner payment form ──
const InnerForm: React.FC<{
  isTrialSubscription: boolean;
  selectedTierProductId?: string;
  total: number;
  subscriptionId: string | null;
}> = ({ isTrialSubscription, selectedTierProductId, total, subscriptionId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [onReady, setOnReady] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [paymentFailure, setPaymentFailure] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { userData } = useSelector((state: any) => state.appData);
  const clientSecret = useSelector((state: any) => state.appData.clientSecretStripe);
  const isSetupIntent = clientSecret?.startsWith('seti_');
  const effectiveIsTrial = isTrialSubscription || isSetupIntent;

  const handleSuccess = async () => {
    // The renewal now defaults to the gym's monthly premium server-side
    // (member_settings), so no /set-renewal-tier call is needed here.
    const value = total;
    trackPurchaseConversionCall({ email: userData?.email, phone: userData?.phone, value, eventId: `${Date.now()}` });
    setShowStatusModal(true);
    setPaymentFailure(false);
  };

  const fail = (msg: string) => { setErrorMsg(msg); setShowStatusModal(true); setPaymentFailure(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setErrorMsg('');
    try {
      if (effectiveIsTrial) {
        const { error, setupIntent } = await stripe.confirmSetup({ elements, confirmParams: { return_url: `${window.location.origin}/payment` }, redirect: 'if_required' });
        if (error) { fail(error.message || 'We couldn’t save your card. Please try again.'); return; }
        if (setupIntent?.status !== 'succeeded') { fail('We couldn’t save your card. Please try again.'); return; }
        const pmId = typeof setupIntent.payment_method === 'string' ? setupIntent.payment_method : setupIntent.payment_method?.id;
        if (pmId && subscriptionId) {
          const { success } = await attachPaymentMethodToSubscriptionCall(subscriptionId, pmId);
          if (!success) { fail('We saved your card but couldn’t start your membership. Please contact support.'); return; }
        }
        await handleSuccess();
      } else {
        const { error, paymentIntent } = await stripe.confirmPayment({ elements, confirmParams: { return_url: `${window.location.origin}/payment` }, redirect: 'if_required' });
        if (error) { fail(error.message || 'Your payment could not be completed. Please check your card and try again.'); return; }
        // Only show success if the payment ACTUALLY succeeded (this check was missing).
        if (paymentIntent?.status !== 'succeeded') { fail('Your payment could not be completed. Please try a different card.'); return; }
        await handleSuccess();
      }
    } catch { fail('Something went wrong. Please try again or contact support.'); }
    finally { setIsProcessing(false); }
  };

  const handleModalClose = () => {
    if (!paymentFailure) {
      setShowStatusModal(false);
      dispatch(emptyBasket());
      dispatch(setLoggedIn(true));
      dispatch(setMemberLoggedIn(true));
      navigate(DASHBOARD_MEMBERS);
    } else {
      setShowStatusModal(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {!onReady && <div style={{ textAlign: 'center', padding: '16px' }}><CircularProgress size={20} /></div>}
      <PaymentElement onReady={() => setOnReady(true)} options={{ layout: { type: 'tabs', defaultCollapsed: false } }} />

      <button
        type="submit"
        disabled={isProcessing || !onReady}
        style={{
          width: '100%', marginTop: '20px', backgroundColor: isProcessing || !onReady ? '#ccc' : '#1a1a1a',
          color: '#fff', border: 'none', borderRadius: '30px', padding: '17px',
          fontSize: '16px', fontWeight: 700, cursor: isProcessing || !onReady ? 'not-allowed' : 'pointer',
        }}
      >
        {isProcessing ? <CircularProgress size={18} style={{ color: '#fff' }} /> : effectiveIsTrial ? 'Start Free Month' : `Pay $${total.toFixed(2)}`}
      </button>

      {showStatusModal && (
        <div onClick={() => paymentFailure && setShowStatusModal(false)} style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: '#faf6f1', borderRadius: '24px', padding: '36px 28px',
            maxWidth: '400px', width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          }}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#d4cbc2', margin: '0 auto 28px' }} />
            {paymentFailure ? (
              <>
                <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#1a1a1a', margin: '0 0 12px', letterSpacing: '-0.3px' }}>
                  Payment failed
                </h2>
                <p style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, margin: '0 0 28px' }}>
                  {errorMsg || "We couldn't process your payment. Please check your card details and try again."}
                </p>
                <button onClick={() => setShowStatusModal(false)} style={{
                  width: '100%', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                  borderRadius: '30px', padding: '16px', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
                }}>Try Again</button>
              </>
            ) : (
              <>
                <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#1a1a1a', margin: '0 0 12px', letterSpacing: '-0.3px' }}>
                  You're all set!
                </h2>
                <p style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, margin: '0 0 28px' }}>
                  Your membership is now active. Welcome to JackedRabbit.
                </p>
                <div style={{ height: '1px', backgroundColor: '#e8e2dc', marginBottom: '20px' }} />
                <button onClick={handleModalClose} style={{
                  width: '100%', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                  borderRadius: '30px', padding: '16px', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
                }}>Go to Dashboard</button>
              </>
            )}
          </div>
        </div>
      )}
    </form>
  );
};

// ── Main Payment View ──
const PaymentView: React.FC = () => {
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [elementsKey, setElementsKey] = useState(Date.now());
  const [isTrialSubscription, setIsTrialSubscription] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [selectedRenewalTier, setSelectedRenewalTier] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const clientSecret = useSelector((state: any) => state.appData.clientSecretStripe);
  const subscriptionId = useSelector((state: any) => state.appData.subscriptionId);
  const { selectedProduct, appliedDiscount, basketProduct } = useSelector((state: any) => state.appData);
  const { idToken } = useSelector((state: any) => state.auth);

  useEffect(() => { return () => { dispatch(clearClientSecretStripe()); dispatch(clearSubscriptionId()); dispatch(clearAppliedDiscount()); }; }, [dispatch]);
  useEffect(() => { trackPixelEvent('InitiateCheckout'); }, []);
  useEffect(() => { if (clientSecret) { setIsLoading(false); setTimeout(() => setElementsKey(Date.now()), 200); } }, [clientSecret]);
  useEffect(() => { const k = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY; if (k) setStripePromise(loadStripe(k)); }, []);

  const createSubscription = useCallback(async () => {
    if (idToken && selectedProduct?.businessProductId && !clientSecret) {
      try {
        setIsLoading(true); setError(null);
        // Send the raw code; the backend resolves it authoritatively (can't be dropped).
        const response = await subscribeToProduct(selectedProduct.businessProductId, appliedDiscount?.code ?? undefined);
        // Trust the backend's authoritative trial flag / payment mode.
        if (response?.isTrialSubscription || response?.paymentMode === 'setup') setIsTrialSubscription(true);
        if (response?.clientSecret) {
          dispatch(setSubscriptionId(response.stripeSubscriptionId));
          dispatch(setClientSecretStripe(response.clientSecret));
        } else { setError('Could not start checkout. Please try again.'); }
      } catch (e: any) {
        // Surface the backend's real reason (cooldown, code used up, not eligible…).
        setError(e?.response?.data?.message || 'We couldn’t start your checkout. Please try again.');
      }
      finally { setIsLoading(false); }
    }
  }, [idToken, selectedProduct, clientSecret, appliedDiscount, dispatch]);

  useEffect(() => { createSubscription(); }, [createSubscription]);

  const retrySubscription = () => {
    if (isRetrying) return;
    setIsRetrying(true);
    dispatch(clearAppliedDiscount()); dispatch(clearClientSecretStripe()); dispatch(clearSubscriptionId());
    setError(null); setIsTrialSubscription(false);
    setTimeout(() => createSubscription().finally(() => setIsRetrying(false)), 500);
  };

  // ── Pricing ──
  const gymProducts: any[] = basketProduct?.products ?? [];
  const isActiveP = (p: any) => p?.is_active !== false && p?.isActive !== false;
  const monthlyPremium = gymProducts.find((p: any) => p.name === 'premium' && isActiveP(p));
  const quarterlyPremium = gymProducts.find((p: any) => p.name === 'premium_quarterly' && isActiveP(p));
  const yearlyPremium = gymProducts.find((p: any) => p.name === 'premium_yearly' && isActiveP(p));
  const renewalTiers: RenewalTier[] = [
    ...(monthlyPremium ? [{ key: 'monthly' as const, label: 'Monthly', rate: Number(monthlyPremium.price?.preTaxCost ?? monthlyPremium.preTaxCost ?? 0), term: '/month', note: 'Billed monthly', savings: null, businessProductId: monthlyPremium.businessProductId }] : []),
    ...(quarterlyPremium ? [{ key: 'quarterly' as const, label: 'Quarterly', rate: Number(quarterlyPremium.price?.preTaxCost ?? quarterlyPremium.preTaxCost ?? 0) * 0.90, term: '/quarter', note: 'Billed every 3 months', savings: 10, businessProductId: quarterlyPremium.businessProductId }] : []),
    ...(yearlyPremium ? [{ key: 'yearly' as const, label: 'Annual', rate: Number(yearlyPremium.price?.preTaxCost ?? yearlyPremium.preTaxCost ?? 0) * 0.85, term: '/year', note: 'Billed annually', savings: 15, businessProductId: yearlyPremium.businessProductId }] : []),
  ];
  const hasMultipleTiers = renewalTiers.length > 1;
  const selectedTierObj = renewalTiers.find(t => t.key === selectedRenewalTier) ?? renewalTiers[0];
  const selectedTierProductId = selectedTierObj?.businessProductId;

  const preTaxCost = Number(selectedProduct?.price?.preTaxCost ?? selectedProduct?.preTaxCost ?? 0);
  const taxRateBps = selectedProduct?.taxRateBps;
  const gymAddr = basketProduct?.address?.fullAddress ?? '';
  const isTampa = /\btampa\b/i.test(gymAddr);
  const taxRate = typeof taxRateBps === 'number' ? taxRateBps / 10000 : isTampa ? 0.075 : 0.07;
  const taxAmt = Math.round(preTaxCost * taxRate * 100) / 100;
  const feeAmt = Math.round((preTaxCost + taxAmt) * 0.035 * 100) / 100;
  const totalBeforeDiscount = preTaxCost + taxAmt + feeAmt;
  let discountAmt = 0;
  if (appliedDiscount) {
    discountAmt = appliedDiscount.type === 'percentage'
      ? Math.round((totalBeforeDiscount * appliedDiscount.amount / 100) * 100) / 100
      : Math.min(appliedDiscount.amount, totalBeforeDiscount);
  }
  const total = Math.max(0, totalBeforeDiscount - discountAmt);

  // ── Gym info ──
  const gymName = basketProduct?.name ?? selectedProduct?.name ?? '';
  const addrParts = gymAddr.split(',').map((s: string) => s.trim());
  const neighborhood = addrParts.length >= 3 ? `${addrParts[1]}, ${addrParts[2]}` : addrParts[0] ?? '';

  const continuesAtPrice = monthlyPremium ? Number(monthlyPremium.price?.preTaxCost ?? monthlyPremium.preTaxCost ?? 0) : null;

  // A trial resolves to a SetupIntent ($0 today). Detect it via the flag, the client
  // secret prefix (set even when a code is applied through DiscountCodeInput), or a $0 total.
  const isFreeTrial = isTrialSubscription || (typeof clientSecret === 'string' && clientSecret.startsWith('seti_')) || total === 0;
  const introDates = (() => {
    const start = new Date();
    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fmt(start)} to ${fmt(end)}`;
  })();

  // ── Shared inline styles (mirrors the mobile checkout) ──
  const eyebrow: CSSProperties = { fontSize: '11px', fontWeight: 700, color: '#8a8178', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 13px' };
  const sect: CSSProperties = { padding: '20px 0', borderBottom: '1px solid #ececea' };
  const optRow: CSSProperties = { display: 'flex', gap: '13px', alignItems: 'flex-start', padding: '14px 0', borderBottom: '1px solid #ececea' };
  const optTag: CSSProperties = { fontSize: '10px', fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', backgroundColor: '#e3d6c2', color: '#594d46', padding: '5px 10px', borderRadius: '20px', minWidth: '58px', textAlign: 'center', flex: '0 0 auto' };
  const optTitle: CSSProperties = { fontSize: '14.5px', fontWeight: 500, color: '#000', lineHeight: 1.4, margin: 0 };
  const optSub: CSSProperties = { fontSize: '12.5px', color: '#8a8178', margin: '3px 0 0', lineHeight: 1.4 };

  // ── UI ──
  return (
    <div style={{ backgroundColor: '#e8e1d5', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: '560px', padding: '30px 26px 42px' }}>
          <button onClick={() => { clearNextProductCall(); navigate('/select-gym'); }} style={{
            width: '38px', height: '38px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.8)',
            border: 'none', fontSize: '20px', color: '#000', cursor: 'pointer', marginBottom: '20px', lineHeight: 1,
          }}>←</button>
          <h1 style={{ fontSize: '32px', fontWeight: 700, color: '#000', letterSpacing: '-0.6px', lineHeight: 1.12, margin: 0 }}>
            Let's upgrade your fitness routine.
          </h1>
          <p style={{ fontSize: '15px', color: '#594d46', lineHeight: 1.5, margin: '12px 0 0' }}>
            Welcome to unlimited month-to-month access at {gymName}.
          </p>
        </div>
      </div>

      {/* Paper card */}
      <div style={{
        flex: 1, backgroundColor: '#faf8f4', borderTopLeftRadius: '28px', borderTopRightRadius: '28px',
        display: 'flex', justifyContent: 'center', marginTop: '-20px',
      }}>
        <div style={{ width: '100%', maxWidth: '560px', padding: '28px 26px 80px' }}>

          {/* Venue card */}
          <div style={{ display: 'flex', gap: '13px', alignItems: 'flex-start', paddingBottom: '20px', borderBottom: '1px solid #ececea' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', backgroundColor: '#d9d2c6', flex: '0 0 auto' }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '16px', fontWeight: 700, color: '#000', margin: 0, lineHeight: 1.25 }}>{gymName}</p>
              <p style={{ fontSize: '13px', color: '#8a8178', margin: '3px 0 0' }}>{neighborhood}</p>
              <p style={{ fontSize: '13px', color: '#594d46', margin: '5px 0 0' }}>{introDates}</p>
            </div>
          </div>

          {/* Have a code */}
          <div style={sect}>
            <p style={eyebrow}>Have a code</p>
            <DiscountCodeInput />
          </div>

          {/* Due today */}
          <div style={sect}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>Total due today</p>
              <p style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>{isFreeTrial ? '$0.00' : `$${total.toFixed(2)}`}</p>
            </div>
            <button onClick={() => setShowBreakdown(v => !v)} style={{ display: 'block', marginLeft: 'auto', marginTop: '4px', background: 'none', border: 'none', fontSize: '13px', color: '#8a8178', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
              {showBreakdown ? 'Hide breakdown' : 'View breakdown'}
            </button>
            {showBreakdown && (
              <div style={{ backgroundColor: '#fff', border: '1px solid #ececea', borderRadius: '14px', padding: '15px', marginTop: '14px' }}>
                {([
                  [isFreeTrial ? 'First month (free)' : 'Intro month', `$${preTaxCost.toFixed(2)}`, false],
                  ...(discountAmt > 0 ? [[`Discount${appliedDiscount?.code ? ` (${appliedDiscount.code})` : ''}`, `-$${discountAmt.toFixed(2)}`, true]] : []),
                  ['Sales tax', `$${taxAmt.toFixed(2)}`, false],
                  ['Service fee', `$${feeAmt.toFixed(2)}`, false],
                ] as [string, string, boolean][]).map(([label, value, green]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '9px' }}>
                    <span style={{ fontSize: '14px', color: green ? '#3a7d44' : '#594d46' }}>{label}</span>
                    <span style={{ fontSize: '14px', color: green ? '#3a7d44' : '#000' }}>{value}</span>
                  </div>
                ))}
              </div>
            )}
            <p style={{ fontSize: '12.5px', color: '#8a8178', margin: '11px 0 0', lineHeight: 1.4 }}>Unlimited class bookings and access to your fitness club.</p>
          </div>

          {/* Next month, it's your choice */}
          <div style={sect}>
            <p style={eyebrow}>Next month, it's your choice</p>
            {continuesAtPrice != null ? (
              <>
                <div style={optRow}>
                  <span style={optTag}>Stay</span>
                  <div style={{ flex: 1 }}>
                    <p style={optTitle}>Continue as a member for <strong style={{ color: '#000' }}>${Math.round(continuesAtPrice)}/month</strong>, plus applicable taxes &amp; fees.</p>
                    <p style={optSub}>Month to month. No term, no contract.</p>
                  </div>
                </div>
                <div style={{ ...optRow, borderBottom: 'none', paddingBottom: 0 }}>
                  <span style={optTag}>Hop</span>
                  <div style={{ flex: 1 }}>
                    <p style={optTitle}>Hop to a new intro offer.</p>
                    <p style={optSub}>Try somewhere new next month at a discount.</p>
                  </div>
                </div>
              </>
            ) : (
              <p style={{ fontSize: '14px', color: '#594d46', lineHeight: 1.5, margin: 0 }}>You can hop to a new gym or cancel anytime.</p>
            )}
          </div>

          {/* Terms */}
          <p style={{ fontSize: '12px', lineHeight: 1.5, color: '#8a8178', margin: '20px 0' }}>
            By submitting your payment you agree to the{' '}
            <a href="https://docs.google.com/document/d/1EKrskAZQ7OXWGVdEkZltad9s3XsCV5yXYdSy-lPpt7g/edit?tab=t.0" target="_blank" rel="noopener noreferrer" style={{ color: '#594d46', textDecoration: 'underline' }}>
              JackedRabbit Terms and Conditions
            </a>.
          </p>

          {/* Payment section */}
          <p style={eyebrow}>Payment</p>

          {isLoading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress size={24} style={{ color: '#1a1a1a' }} />
            </div>
          )}

          {error && !isLoading && (
            <div style={{ color: '#dc2626', textAlign: 'center', padding: '12px 0' }}>
              <p style={{ margin: '0 0 12px' }}>{error}</p>
              <button onClick={retrySubscription} disabled={isRetrying} style={{
                padding: '10px 24px', backgroundColor: isRetrying ? '#ccc' : '#1a1a1a',
                color: '#fff', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: 600,
              }}>
                {isRetrying ? 'Retrying…' : 'Try Again'}
              </button>
            </div>
          )}

          {clientSecret && stripePromise && !isLoading && !error && (
            <Elements key={elementsKey} stripe={stripePromise} options={{
              clientSecret,
              appearance: {
                theme: 'flat',
                variables: { fontFamily: '"Gill Sans", sans-serif', fontLineHeight: '1.5', borderRadius: '10px', colorBackground: '#F6F8FA', accessibleColorOnColorPrimary: '#262626' },
                rules: {
                  '.Block': { backgroundColor: 'var(--colorBackground)', boxShadow: 'none', padding: '12px' },
                  '.Input': { padding: '12px' },
                  '.Tab': { padding: '10px 12px 8px 12px', border: 'none' },
                  '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': { border: 'none', backgroundColor: '#fff', boxShadow: '0 0 0 1.5px var(--colorPrimaryText)' },
                  '.Label': { fontWeight: '500' },
                },
              },
            }}>
              <InnerForm
                isTrialSubscription={isTrialSubscription}
                selectedTierProductId={selectedTierProductId}
                total={total}
                subscriptionId={subscriptionId}
              />
            </Elements>
          )}

          {!clientSecret && !isLoading && !error && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress size={24} style={{ color: '#1a1a1a' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentView;
